import { debounce, isNil } from 'lodash';
import React, {
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { MAX_INTEGER } from '../../config/constants';
import { colors } from '../../config/styles';
import { countDigitsAfterDecimal } from '../../utils/numbers';
import { SvgIcon } from '../Icon';
import KubitTooltip from '../KubitTooltip';
import { defaultInputStyles } from './inputStyles';
import { IFieldDesignProps, IStyledFieldDesignProps } from './types';
import toastMessage from '../../utils/toastMessage';

export interface INumberInputFieldProps
  extends Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'onChange' | 'value' | 'onKeyPress' | 'defaultValue'
    >,
    IFieldDesignProps {
  value: number | null;
  onChange?: (value: number | null, isError?: boolean) => void;
  min?: number;
  max?: number;
  precision?: number;
  errorMessage?: string;
  suffixAction?: React.ReactNode;
  className?: string;
}

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<IStyledFieldDesignProps>`
  ${defaultInputStyles};
  width: 75px;

  ${({ $hasError }) =>
    $hasError &&
    css`
      padding-right: 20px;
      border-color: ${colors.mainError} !important;
    `}
`;

const ErrorIcon = styled(SvgIcon)`
  position: absolute;
  top: 8px;
  right: 6px;
`;

export default forwardRef<HTMLInputElement, INumberInputFieldProps>(
  (
    {
      onChange,
      value,
      min = 0,
      max = MAX_INTEGER,
      precision = 0,
      hasError,
      errorMessage,
      suffixAction,
      className,
      ...props
    },
    ref,
  ) => {
    const [hasInternalError, setHasInternalError] = useState(false);
    const [internalValue, setInternalValue] = useState('');

    const validateNumberValueString = useCallback(
      (newValue: string) => {
        if (newValue === '') {
          return true;
        }

        if (precision > 0 && countDigitsAfterDecimal(newValue) > precision) {
          return false;
        }

        const numberValue = parseFloat(newValue);
        return numberValue >= min && numberValue <= max;
      },
      [precision, min, max],
    );

    // handle cases where input values change from outside this component
    // eg. reset builder
    useEffect(() => {
      if (isNil(value)) {
        setInternalValue('');
        setHasInternalError(false);
      } else {
        const propValueString = value.toString();
        setInternalValue(propValueString);
        const isValidNumber = validateNumberValueString(propValueString);
        setHasInternalError(!isValidNumber);
      }
    }, [value, validateNumberValueString]);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === ',' && precision > 0) {
        event.preventDefault();
        toastMessage({
          message: 'Use a period (.) for decimal values.',
          type: 'error',
        });
      } else if (event.key === '-' || event.key === '.') {
        // allowed special characters
        if (event.key === '-' && !isNil(min) && min >= 0) {
          event.preventDefault();
        }

        if (event.key === '.' && !isNil(precision) && precision <= 0) {
          event.preventDefault();
        }
      } else {
        // Only integers allowed
        if (!/^\d+$/.test(event.key)) {
          event.preventDefault();
        }

        // only allow up to 16 digits. 17 digits breaks parseFloat in javascript
        const newValue = event.currentTarget.value + event.key;
        if (newValue.length > 15) {
          event.preventDefault();
        }
      }
    };

    const handleValueChange = debounce(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        const isValidNumber = validateNumberValueString(newValue);
        const isError = !isValidNumber;
        setHasInternalError(isError);
        const numberValue = parseFloat(newValue);

        if (!isNaN(numberValue)) {
          onChange?.(numberValue, isError);
        } else {
          onChange?.(null, isError);
        }
      },
      1000,
      {
        leading: true, // the default is false
        trailing: true, // the default is true
      },
    );

    // if there is an error provided by the parent or an internal detected error,
    // show the error icon
    const hasValueError = hasError || hasInternalError;

    const internalErrorMessage = useMemo(() => {
      if (hasInternalError) {
        return `This input accepts values between ${min} and ${max}${
          precision > 0
            ? `, with up to ${precision} decimal places allowed`
            : ''
        }`;
      }
      if (hasError && errorMessage?.length) {
        return errorMessage;
      }
    }, [hasError, hasInternalError, min, max, precision, errorMessage]);

    return (
      <InputContainer className={className}>
        <Input
          {...props}
          ref={ref}
          value={internalValue}
          onChange={handleValueChange}
          onInput={(ev: React.ChangeEvent<HTMLInputElement>) =>
            setInternalValue(ev.target.value)
          }
          onKeyPress={handleKeyPress}
          $hasError={hasValueError}
        />
        {hasValueError && (
          <KubitTooltip content={internalErrorMessage} noOpenDelay={true}>
            <ErrorIcon name="error-icon" width={12} height={12} />
          </KubitTooltip>
        )}
        {suffixAction && !hasValueError && suffixAction}
      </InputContainer>
    );
  },
);
